import { c as _c } from "react/compiler-runtime";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { AppHeaderBar } from "@/components/ui/app-header";
import { cn } from "@repo/blacksmith-ui/cn";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@repo/blacksmith-ui/table";
import { TestListSkeleton } from "@/components/TestListSkeleton";

/** Run Details skeleton (slideout + full page). Matches DESIGN.md: full-width, no layout shift; Skeleton only for icons. */
const SUMMARY_STRIP_ITEMS = 5;
const TABLE_SKELETON_ROWS = 3;
export type RunDetailsSkeletonVariant = "overview" | "tests";
interface RunDetailsSkeletonProps {
  /** When "tests", show two-pane Tests layout instead of overview table. */
  variant?: RunDetailsSkeletonVariant;
}
export default function RunDetailsSkeleton(t0) {
  const $ = _c(13);
  const {
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "overview" : t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton className="h-5 w-16 shrink-0 rounded" />;
    t3 = <Skeleton className="h-4 w-3 shrink-0 rounded" />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <AppHeaderBar as="header" height="min" className="relative bg-app-background"><div className="flex flex-1 items-center flex-wrap px-8 gap-2 min-w-0"><div className="flex flex-1 items-center gap-4 min-w-0">{t2}{t3}<div className="flex items-center gap-2 min-w-0"><Skeleton className="h-6 w-6 shrink-0 rounded-full" /><Skeleton className="h-6 w-48 max-w-full rounded" /><Skeleton className="h-4 w-4 shrink-0 rounded" /></div></div></div></AppHeaderBar>;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton className="h-8 w-24 rounded-lg shrink-0" />;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="min-w-0 flex-1 overflow-x-auto flex items-center gap-1">{t5}<div className="flex items-center gap-1 shrink-0"><Skeleton className="h-8 w-16 rounded-lg" /><Skeleton className="h-4 min-w-[16px] rounded-full" /></div></div>;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="px-6 pt-app-header-gap pb-0 bg-app-background flex-shrink-0"><div className="flex items-center gap-4 flex-wrap min-w-0">{t6}<div className="flex items-center gap-2 flex-shrink-0">{Array.from({
            length: SUMMARY_STRIP_ITEMS
          }, _temp)}</div></div></div>;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const t8 = variant !== "tests" && "p-6";
  let t9;
  if ($[6] !== t8) {
    t9 = cn("flex-1 min-h-0 overflow-y-auto bg-app-background", t8);
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== variant) {
    t10 = variant === "tests" ? <TestListSkeleton /> : <div className="w-full border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] overflow-hidden"><Table className="table-fixed w-full"><colgroup><col style={{
            width: "55%"
          }} /><col style={{
            width: "30%"
          }} /><col style={{
            width: "15%"
          }} /></colgroup><TableHeader className="px-3 py-2 bg-[var(--color-surface)]"><TableRow className="border-b border-[var(--color-border)]"><TableHead className="h-8 w-[55%] text-xs uppercase tracking-wider"><Skeleton className="h-4 w-16 rounded" /></TableHead><TableHead className="h-8 w-[30%] text-xs uppercase tracking-wider"><Skeleton className="h-4 w-14 rounded" /></TableHead><TableHead className="h-8 w-[15%] text-xs uppercase tracking-wider"><Skeleton className="h-4 w-16 rounded" /></TableHead></TableRow></TableHeader><TableBody className="divide-y divide-[var(--color-border)]">{Array.from({
            length: TABLE_SKELETON_ROWS
          }, _temp2)}</TableBody></Table></div>;
    $[8] = variant;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t10 || $[11] !== t9) {
    t11 = <div className="flex flex-col flex-1 min-h-0 w-full min-w-0 bg-app-background">{t4}{t7}<div className={t9}>{t10}</div></div>;
    $[10] = t10;
    $[11] = t9;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  return t11;
}
function _temp2(__0, i_0) {
  return <TableRow key={i_0} className="border-0"><TableCell className="py-2 px-3 w-[55%]"><div className="flex items-center gap-2"><Skeleton className="h-4 w-4 shrink-0 rounded-full" /><Skeleton className="h-4 w-32 rounded" /></div></TableCell><TableCell className="py-2 px-3 w-[30%]"><Skeleton className="h-4 w-28 rounded" /></TableCell><TableCell className="py-2 px-3 w-[15%]"><div className="flex items-center gap-2 justify-end"><Skeleton className="h-4 w-8 rounded" /><Skeleton className="h-2 flex-1 max-w-12 rounded" /></div></TableCell></TableRow>;
}
function _temp(_, i) {
  return <div key={i} className={`flex flex-col gap-0.5 items-start min-w-0 border-[var(--color-border)] px-2 py-0 ${i === 0 ? "border-l-0 pl-0" : "border-l"}`}><Skeleton className="h-3 w-12 rounded" /><Skeleton className="h-4 w-16 rounded" /></div>;
}